import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { CustomerInfoService } from '../../pages/customer-info/customer-info.service';

@Component({
  selector: 'app-customer-search',
  templateUrl: './customer-search.component.html',
  styleUrls: ['./customer-search.component.scss'],
})
export class CustomerSearchComponent implements OnInit, OnDestroy {
  @ViewChild('autoInput') autoInput!: ElementRef;
  @ViewChild('shortAutoInput') shortAutoInput!: ElementRef;

  @Input() placeholder = '请输入客户编号、客户名称、地址等信息查询';
  @Output() selectCustomer = new EventEmitter<any>();

  searchCustomer?: string;
  custSearchResultList: any[] = [];
  custSearchResultLength = 0;
  isWithholdingSearching = false;
  isShowSearch = false;
  curHoverList = 0;
  searchHistoryList: any[] = [];

  private membersSearch = new Subject<string>();
  private searchSubscription?: Subscription;

  readonly magicLabel = '__NZORRO_AUTO_FILL__'; // 魔法值标记，用于识别 ng-zorro 的自动填充

  private readonly STORAGE_KEY = 'customer_search_history';
  private readonly MAX_HISTORY = 10;

  constructor(private customerService: CustomerInfoService) {}

  ngOnInit() {
    this.loadSearchHistory();
    this.initSearchSubscription();
    this.interceptInputValueSetter();
    document.addEventListener('keyup', this.appKeyUpEvent);
  }

  ngOnDestroy() {
    document.removeEventListener('keyup', this.appKeyUpEvent);
    this.searchSubscription?.unsubscribe();
  }

  appKeyUpEvent = (e: KeyboardEvent) => {
    // 只处理上下箭头键
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      return;
    }

    const displayType = this.getSearchDisplayType();
    let listLength = 0;
    if (displayType === 'history') {
      listLength = this.searchHistoryList.length;
    } else if (displayType === 'result') {
      listLength = this.custSearchResultLength;
    }

    if (listLength === 0) {
      return;
    }

    e.preventDefault();

    const keyDeltaMap: Record<string, number> = {
      ArrowUp: -1,
      ArrowDown: 1,
    };
    const delta = keyDeltaMap[e.key];
    this.curHoverList = (this.curHoverList + delta + listLength) % listLength;
  };

  loadSearchHistory() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      this.searchHistoryList = raw ? JSON.parse(raw) : [];
    } catch {
      this.searchHistoryList = [];
    }
  }

  saveSearchHistory(json: string) {
    if (!json) return;

    try {
      const item = JSON.parse(json);
      if (!item?.custCode) return;

      // 去重：移除已有的相同 custCode 记录
      const list = this.searchHistoryList.filter((h: any) => h.custCode !== item.custCode);

      // 最新记录插入头部，限制最大条数
      list.unshift(item);
      if (list.length > this.MAX_HISTORY) {
        list.length = this.MAX_HISTORY;
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
      this.searchHistoryList = list;
    } catch {
      // ignore malformed json
    }
  }

  removeSearchHistory(custCode: string) {
    this.searchHistoryList = this.searchHistoryList.filter((h: any) => h.custCode !== custCode);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.searchHistoryList));
  }

  clearSearchHistory() {
    this.searchHistoryList = [];
    localStorage.removeItem(this.STORAGE_KEY);
  }

  onSearchInputFocus() {
    this.curHoverList = 0; // 重置高亮索引
    this.loadSearchHistory();
  }

  searchCust(value: string) {
    // 忽略 ng-zorro autocomplete 选中后的回填值
    if (value === this.magicLabel) {
      this.searchCustomer = '';
      return;
    }

    if (!value || typeof value !== 'string') {
      this.searchCustomer = '';
      return;
    }

    this.searchCustomer = value;

    this.isWithholdingSearching = true;
    this.membersSearch.next(value);
  }

  onSelectOption(event: any) {
    // 处理选项选择（由 nz-autocomplete 的 selectionChange 触发）
    const selectedValue = event.nzValue;

    let customerData = selectedValue;

    // 如果是历史记录，提取真实数据
    if (selectedValue && selectedValue.isHistory) {
      customerData = selectedValue.event;
    } else {
      // 如果不是历史记录，保存到历史
      this.saveSearchHistory(JSON.stringify(customerData));
    }

    // 触发选择事件
    this.selectCustomer.emit(customerData);

    // 清空搜索状态
    this.searchCustomer = '';
    this.isWithholdingSearching = false;
    this.custSearchResultLength = 0;
    this.custSearchResultList = [];

    // 延迟失焦，确保 autocomplete 面板关闭后再执行
    setTimeout(() => {
      this.autoInput?.nativeElement.blur();
    });
  }

  showSearch() {
    this.isShowSearch = true;
    setTimeout(() => {
      this.autoInput?.nativeElement.focus();
    }, 200);
  }

  hiddenSearch() {
    if (!this.searchCustomer) {
      this.isShowSearch = false;
      setTimeout(() => {
        this.shortAutoInput?.nativeElement.focus();
      }, 200);
    }
  }

  /**
   * 判断当前搜索面板应显示的内容类型
   * - 'history': 无搜索词时显示历史记录
   * - 'loading': 正在搜索中
   * - 'result': 有搜索结果时显示搜索列表
   * - 'empty': 有搜索词但无结果时显示空状态
   */
  getSearchDisplayType(): 'history' | 'result' | 'empty' | 'loading' {
    if (!this.searchCustomer || this.searchCustomer.length === 0) {
      return 'history';
    }
    if (this.isWithholdingSearching) {
      return 'loading';
    }
    if (this.custSearchResultLength === 0) {
      return 'empty';
    }
    return 'result';
  }

  getCustStateLabel(state: number): string {
    const stateMap: Record<number, string> = {
      1: '新装',
      2: '停用',
      3: '注销',
      4: '停水',
    };
    return stateMap[state] || '';
  }

  initSearchSubscription() {
    this.searchSubscription = this.membersSearch
      .pipe(
        debounceTime(500),
        switchMap((keyword: any) => {
          if (!keyword || typeof keyword !== 'string' || keyword.length < 2) {
            return of({ items: [] as any[] });
          }

          return this.customerService.getCustomerDetailByField(keyword);
        }),
        map((response: any) => {
          this.isWithholdingSearching = false;
          this.curHoverList = 0;
          this.custSearchResultLength = response.items.length;
          return response.items;
        }),
      )
      .subscribe((items) => {
        this.custSearchResultList = items;
      });
  }

  // 劫持输入框的 value setter，阻止 ng-zorro 的自动填充
  interceptInputValueSetter() {
    // 等待 ViewChild 初始化
    setTimeout(() => {
      if (!this.autoInput?.nativeElement) return;

      const input = this.autoInput.nativeElement;
      const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(input), 'value');

      if (!descriptor) return;

      const originalSetter = descriptor.set;
      const self = this;

      // 重写 value setter
      Object.defineProperty(input, 'value', {
        get: descriptor.get,
        set: function (newValue: string) {
          // 如果是魔法值，说明是 ng-zorro 自动填充，直接清空
          if (newValue === self.magicLabel) {
            if (originalSetter) {
              originalSetter.call(this, '');
            }
            return;
          }
          // 否则正常调用原始 setter
          if (originalSetter) {
            originalSetter.call(this, newValue);
          }
        },
        configurable: true,
        enumerable: true,
      });
    });
  }
}
